/**
 * VFXAbilityDefs - Unique VFX effect definitions for all abilities
 *
 * This file contains data-driven VFX definitions for every ability in the game.
 * Each effect is designed to be visually distinct and match the ability's fantasy.
 *
 * Organization by class:
 * - General/Melee (basic attacks, common effects)
 * - Warrior (melee combat, heavy attacks)
 * - Mage (elemental magic, buffs)
 * - Paladin (holy magic, auras, heals)
 * - Hunter (ranged attacks, traps, nature)
 * - Rogue/Thief (shadow, poison, ninja)
 *
 * To add a new effect:
 * 1. Define a VFXEffectConfig object below
 * 2. Add it to ABILITY_VFX_EFFECTS array
 * 3. Import and register in VFXEffectRegistry
 */

import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { ringBlast, slashArc, shardBurst, glintScatter } from './burstBuilders';

// =============================================================================
// GENERAL / MELEE EFFECTS
// =============================================================================

/**
 * Basic melee slash - quick red arc for basic strikes
 */
const MELEE_SLASH: VFXEffectConfig = {
  id: 'melee_slash',
  name: 'Melee Slash',
  category: 'burst',
  description: 'Quick red arc for basic melee strikes',
  primitives: [
    {
      type: 'ellipse',
      width: 50,
      height: 8,
      color: 0xFF3333,
      alpha: 0.9,
      depth: 95,
      tween: {
        rotation: Math.PI * 0.5,
        alpha: 0,
        scaleX: 1.5,
        scaleY: 0.5,
        duration: 150,
        ease: 'Power2'
      }
    },
    {
      type: 'circle',
      radius: 6,
      color: 0xFF6666,
      alpha: 0.7,
      depth: 96,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 200,
        ease: 'Power2'
      },
      scatter: { count: 4, radius: 20 }
    }
  ]
};

/**
 * Heavy impact - dark red burst for execute/heavy hits
 */
const HEAVY_IMPACT: VFXEffectConfig = {
  id: 'heavy_impact',
  name: 'Heavy Impact',
  category: 'burst',
  description: 'Dark red burst for execute and heavy melee hits',
  primitives: [
    {
      type: 'circle',
      radius: 35,
      color: 0x8B0000,
      alpha: 0.8,
      depth: 95,
      tween: {
        alpha: 0,
        scale: 2.5,
        duration: 400,
        ease: 'Power2'
      }
    },
    {
      type: 'ellipse',
      width: 60,
      height: 10,
      color: 0xFF0000,
      alpha: 0.6,
      depth: 94,
      tween: {
        alpha: 0,
        scaleX: 3,
        scaleY: 0.3,
        duration: 350,
        ease: 'Power2'
      }
    },
    {
      type: 'circle',
      radius: 5,
      color: 0x660000,
      alpha: 0.9,
      depth: 96,
      tween: {
        alpha: 0,
        scale: 3,
        duration: 500,
        ease: 'Power2'
      },
      scatter: { count: 8, radius: 40 }
    }
  ],
  screenShake: { intensity: 10, duration: 300 }
};

/**
 * Holy flash - bright cyan/gold burst for holy strikes
 */
const HOLY_FLASH: VFXEffectConfig = {
  id: 'holy_flash',
  name: 'Holy Flash',
  category: 'burst',
  description: 'Bright cyan and gold burst for holy melee strikes',
  primitives: [
    {
      type: 'circle',
      radius: 40,
      color: 0xFFD700,
      alpha: 0.8,
      depth: 95,
      tween: {
        alpha: 0,
        scale: 2.5,
        duration: 400,
        ease: 'Power2'
      }
    },
    {
      type: 'circle',
      radius: 25,
      color: 0xFFF8DC,
      alpha: 0.7,
      depth: 96,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 350,
        ease: 'Power2'
      }
    },
    {
      type: 'star',
      points: 5,
      innerRadius: 10,
      outerRadius: 35,
      color: 0xFFFFFF,
      alpha: 0.9,
      depth: 97,
      tween: {
        rotation: Math.PI * 0.5,
        alpha: 0,
        scale: 1.5,
        duration: 400,
        ease: 'Power2'
      }
    }
  ]
};

/**
 * Holy sparkle - small golden sparkles for holy effects
 */
const HOLY_SPARKLE: VFXEffectConfig = {
  id: 'holy_sparkle',
  name: 'Holy Sparkle',
  category: 'burst',
  description: 'Small golden sparkles for holy projectile muzzle',
  primitives: [
    {
      type: 'circle',
      radius: 12,
      color: 0xFFFFCC,
      alpha: 0.8,
      depth: 95,
      tween: {
        alpha: 0,
        scale: 2,
        duration: 200,
        ease: 'Power2'
      }
    },
    {
      type: 'star',
      points: 4,
      innerRadius: 4,
      outerRadius: 10,
      color: 0xFFD700,
      alpha: 0.9,
      depth: 96,
      tween: {
        rotation: Math.PI * 0.25,
        alpha: 0,
        scale: 1.5,
        duration: 250,
        ease: 'Power2'
      },
      scatter: { count: 5, radius: 15 }
    }
  ]
};

// =============================================================================
// CROSS-CLASS SHARED BURSTS (FX grammar buildout lane 3, 2026-07-17)
// TOML-authored names that previously auto-realized as generic school bursts.
// Static registration wins over namedEffectGenerator fallback.
// NOTE: 'fire_burst' / 'arcane_burst' also exist as ParticleEffects.ts ids —
// that is the particleEffectId namespace (emitter configs), NOT this registry;
// no key collision.
// =============================================================================

/**
 * Arcane burst - sharp arcane geometry. Violet star-ring blast with
 * crystalline shard slivers and white glints. SHARP shapes throughout
 * (star/polygon), deliberately not round — arcane reads as geometry.
 */
const ARCANE_BURST: VFXEffectConfig = {
  id: 'arcane_burst',
  name: 'Arcane Burst',
  category: 'burst',
  description: 'Violet star-ring blast with crystalline shards and white glints',
  primitives: [
    // Bespoke flavor: the sharp 6-point star flash that IS the arcane read
    {
      type: 'star', points: 6, innerRadius: 8, outerRadius: 26,
      color: 0xCC66FF, alpha: 0.95, depth: 96, glow: true,
      tween: { rotation: Math.PI / 3, alpha: 0, scale: 1.6, duration: 280, ease: 'Power2' },
    },
    ringBlast({
      radius: 20, color: 0xAA00FF, alpha: 0.55, depth: 94,
      stroke: { color: 0xCC66FF, width: 2 }, expandTo: 2.0, durationMs: 260,
    }),
    shardBurst({
      count: 6, spread: 26, color: 0xAA00FF, depth: 95, glow: true,
      shard: { points: [0, -9, 3, 5, -3, 5] }, durationMs: 300,
    }),
    glintScatter({ count: 5, spread: 30, color: 0xFFFFFF, durationMs: 200, delayMs: 40 }),
  ],
};

/**
 * Fire burst - a hot COMPACT fireball pop. Double ring (red-orange core,
 * amber edge) + white-hot pinpoint + rising embers. Deliberately smaller,
 * hotter, and faster than hit_fire (r30 / 300-350ms / entity tint /
 * screenFlash) — this is the muzzle/impact pop, not the full hit landing.
 */
const FIRE_BURST: VFXEffectConfig = {
  id: 'fire_burst',
  name: 'Fire Burst',
  category: 'burst',
  description: 'Hot compact fireball pop — double ring plus rising embers',
  primitives: [
    ringBlast({
      radius: 12, color: 0xFF4500, alpha: 0.85, depth: 95, glow: true,
      expandTo: 1.9, durationMs: 180, ease: 'Power3',
    }),
    ringBlast({ radius: 18, color: 0xFFAA33, alpha: 0.55, depth: 94, expandTo: 2.1, durationMs: 230 }),
    // Bespoke: white-hot pinpoint core that snaps shut
    {
      type: 'circle', radius: 5, color: 0xFFE9B8, alpha: 0.95, depth: 97, glow: true,
      tween: { alpha: 0, scale: 0.4, duration: 160, ease: 'Power1' },
    },
    // Bespoke: rising embers (scatter + upward drift)
    {
      type: 'circle', radius: 2.5, color: 0xFFAA33, alpha: 0.9, depth: 96, glow: true,
      scatter: { count: 6, radius: 14 },
      tween: { y: -28, alpha: 0, scale: 0.5, duration: 260, ease: 'Sine.easeOut' },
    },
  ],
};

/**
 * Frost shatter - ice SHATTER. Two layers of pale-blue polygon slivers with
 * white strokes thrown outward + one crisp thin white-stroked ring. Brittle
 * and angular — nothing soft or round beyond the ring outline.
 */
const FROST_SHATTER: VFXEffectConfig = {
  id: 'frost_shatter',
  name: 'Frost Shatter',
  category: 'burst',
  description: 'Brittle pale-blue shard shatter with a crisp thin ring',
  primitives: [
    ringBlast({
      radius: 22, color: 0x99DDFF, alpha: 0.35, depth: 93,
      stroke: { color: 0xFFFFFF, width: 2 }, expandTo: 1.9, durationMs: 240,
    }),
    shardBurst({
      count: 7, spread: 30, color: 0x99DDFF, alpha: 0.95, depth: 95,
      shard: { points: [0, -11, 4, 4, -3, 6] },
      stroke: { color: 0xFFFFFF, width: 1 }, durationMs: 320,
    }),
    shardBurst({
      count: 5, spread: 18, color: 0xCCF0FF, alpha: 0.9, depth: 96,
      shard: { points: [0, -6, 2, 3, -2, 3] },
      stroke: { color: 0xFFFFFF, width: 1 }, durationMs: 260,
    }),
    glintScatter({ count: 4, spread: 24, color: 0xFFFFFF, radius: 2, durationMs: 200, delayMs: 60 }),
  ],
};

/**
 * Quick slash - a fast light slash shared by THREE classes, so it stays
 * class-neutral: 2 fills only (blade white + faint gray follow) so
 * per-ability authored colors can tint it without fighting a palette.
 */
const QUICK_SLASH: VFXEffectConfig = {
  id: 'quick_slash',
  name: 'Quick Slash',
  category: 'burst',
  description: 'Fast thin white blade arc with a faint gray follow-through',
  primitives: [
    slashArc({
      radius: 34, startDeg: -50, endDeg: 40, width: 3,
      color: 0xEEEEEE, alpha: 0.95, depth: 96, glow: true, durationMs: 150,
    }),
    slashArc({
      radius: 30, startDeg: -50, endDeg: 40, width: 5,
      color: 0xAAAAAA, alpha: 0.35, depth: 95, durationMs: 190, fadeDelayMs: 40,
    }),
    glintScatter({ count: 3, spread: 22, color: 0xEEEEEE, radius: 2, durationMs: 160 }),
  ],
};

// =============================================================================
// EXPORTS
// =============================================================================

export const COMMON_VFX: VFXEffectConfig[] = [
  MELEE_SLASH,
  HEAVY_IMPACT,
  HOLY_FLASH,
  HOLY_SPARKLE,
  ARCANE_BURST,
  FIRE_BURST,
  FROST_SHATTER,
  QUICK_SLASH,
];
